/**
 * OffersForCandidate — full offer builder for a single candidate.
 *
 * Recruiter flow:
 *   1. Enter candidate's current CTC + expected CTC.
 *   2. Generate scenarios from presets (Desired/Balanced/Aggressive/Low) or
 *      add a custom one. Each scenario shows Fixed / Variable / Deductions
 *      side-by-side with Annual and Monthly amounts.
 *   3. Override any line; lock rows so they survive a re-distribute.
 *   4. Add justification per scenario.
 *   5. Send the bundle for employer approval.
 *
 * Employer flow:
 *   6. Approve or reject the whole bundle with a comment.
 *
 * Post-approval:
 *   7. Recruiter records candidate outcome (accepted scenario / declined /
 *      still negotiating). Excel export per scenario.
 *
 * Backed by:
 *   GET    /api/candidates/{cid}/offers
 *   POST   /api/offers
 *   PATCH  /api/offers/{oid}
 *   POST   /api/offers/{oid}/scenarios/generate
 *   PATCH  /api/offers/{oid}/scenarios/{sid}
 *   DELETE /api/offers/{oid}/scenarios/{sid}
 *   POST   /api/offers/{oid}/send-for-approval
 *   POST   /api/offers/{oid}/approve | /reject
 *   POST   /api/offers/{oid}/candidate-outcome
 *   GET    /api/offers/{oid}/scenarios/{sid}/export.xlsx
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
    ArrowLeft, Plus, Trash2, Sparkles, Send, CheckCircle2, XCircle, Download,
    Lock, Unlock, RefreshCcw, MessageSquare, Wand2, BarChart3, ListChecks,
    FileSpreadsheet, Loader2,
} from "lucide-react";

const fmt = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
    return `₹${v.toLocaleString("en-IN")}`;
};
const fmtFull = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STATUS_PILL = {
    draft: { label: "Draft", cls: "bg-secondary text-foreground/70 border-border" },
    awaiting_approval: { label: "Awaiting approval", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    accepted: { label: "Candidate accepted", cls: "bg-ikb text-white border-ikb" },
    declined: { label: "Candidate declined", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    negotiating: { label: "Negotiating", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const COLOR_BAND = {
    blue: "border-l-ikb", emerald: "border-l-emerald-500", rose: "border-l-rose-500",
    amber: "border-l-amber-500", violet: "border-l-violet-500", grey: "border-l-slate-400",
};

// Hoisted out of `ScenarioCard` — defining this inline inside the parent
// component causes React to see a NEW component type every keystroke and
// fully unmount/remount the input tree, killing focus mid-type. This is the
// classic "function component declared inside another component" anti-pattern
// and was the root cause of the offer-scenarios focus-loss bug.
function ScenarioGroup({ title, items, group, locked, scenarioId, onEditComp, onRemoveComp, onAddRow }) {
    return (
        <div className="border border-border bg-white">
            <div className="px-3 py-2 bg-secondary/50 border-b border-border flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">{title}</span>
                {!locked && (
                    <button
                        onClick={() => onAddRow(group)}
                        className="text-[10px] font-mono text-ikb hover:underline inline-flex items-center gap-1"
                        data-testid={`sc-${scenarioId}-add-${group}`}
                    >
                        <Plus className="w-3 h-3" /> Add
                    </button>
                )}
            </div>
            <div>
                {items.length === 0 && <div className="px-3 py-3 text-[11px] text-foreground/40">No rows</div>}
                {items.map((c) => (
                    <div key={c.id} className="grid grid-cols-[1fr_110px_110px_30px_30px] gap-1 items-center px-2 py-1.5 border-b border-border/40 last:border-0 hover:bg-secondary/30" data-testid={`sc-${scenarioId}-row-${c.key}`}>
                        <Input value={c.label} onChange={(e) => onEditComp(c.id, { label: e.target.value })} disabled={locked} className="rounded-none h-8 text-xs" />
                        <Input type="number" value={c.monthly_amount} onChange={(e) => onEditComp(c.id, { monthly_amount: Number(e.target.value || 0) })} disabled={locked} className="rounded-none h-8 text-xs text-right font-mono" data-testid={`sc-${scenarioId}-row-${c.key}-monthly`} />
                        <Input type="number" value={c.annual_amount} onChange={(e) => onEditComp(c.id, { annual_amount: Number(e.target.value || 0) })} disabled={locked} className="rounded-none h-8 text-xs text-right font-mono" data-testid={`sc-${scenarioId}-row-${c.key}-annual`} />
                        <button onClick={() => onEditComp(c.id, { locked: !c.locked })} disabled={locked} title={c.locked ? "Unlock (will be overwritten on redistribute)" : "Lock (will survive redistribute)"} className={`p-1 ${c.locked ? "text-ikb" : "text-foreground/40 hover:text-foreground"}`}>
                            {c.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => onRemoveComp(c.id)} disabled={locked} className="text-rose-500 hover:bg-rose-50 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ScenarioCard({ scenario, onSave, onDelete, onExport, onRedistribute, locked }) {
    // Local editable state — only pushed to the server when user clicks Save.
    // This prevents auto-save reloads from killing focus while typing.
    const [local, setLocal] = useState(scenario);
    const [saving, setSaving] = useState(false);

    // Reset local state whenever the scenario identity changes OR
    // its server-side fingerprint changes (e.g. after a save / redistribute).
    useEffect(() => {
        setLocal(scenario);
    }, [scenario.id, scenario.updated_at, scenario.totals?.gross_annual, scenario.components?.length]);

    const dirty = useMemo(() => JSON.stringify(local) !== JSON.stringify(scenario), [local, scenario]);

    const updateLocal = (patch) => setLocal((prev) => ({ ...prev, ...patch }));

    const editComp = (compId, patch) => {
        const components = (local.components || []).map((c) => {
            if (c.id !== compId) return c;
            const next = { ...c, ...patch };
            if (patch.annual_amount !== undefined) next.monthly_amount = Math.round((Number(patch.annual_amount) || 0) / 12 * 100) / 100;
            if (patch.monthly_amount !== undefined) next.annual_amount = Math.round((Number(patch.monthly_amount) || 0) * 12 * 100) / 100;
            return next;
        });
        updateLocal({ components });
    };

    const removeComp = (compId) => updateLocal({ components: local.components.filter((c) => c.id !== compId) });

    const addRow = (group) => {
        const newRow = {
            id: `tmp_${Date.now()}`, key: `custom_${Date.now()}`,
            label: "New line", group, calc_type: "manual", value: null,
            monthly_amount: 0, annual_amount: 0, notes: "", locked: false,
        };
        updateLocal({ components: [...(local.components || []), newRow] });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(local.id, {
                name: local.name,
                increment_pct: Number(local.increment_pct || 0),
                justification: local.justification || "",
                components: local.components || [],
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDiscard = () => setLocal(scenario);

    const handleRedistribute = async () => {
        if (dirty && !window.confirm("Re-distribute will recompute components from your increment %. Unsaved field edits will be replaced. Continue?")) return;
        setSaving(true);
        try {
            await onRedistribute(local.id, Number(local.increment_pct || 0));
        } finally {
            setSaving(false);
        }
    };

    const grouped = useMemo(() => ({
        fixed: (local.components || []).filter((c) => c.group === "fixed"),
        variable: (local.components || []).filter((c) => c.group === "variable"),
        deduction: (local.components || []).filter((c) => c.group === "deduction"),
        benefit: (local.components || []).filter((c) => c.group === "benefit"),
    }), [local.components]);

    // Live totals based on LOCAL edits so the user sees their changes immediately.
    const liveTotals = useMemo(() => {
        const sum = (g, k) => (local.components || []).filter((c) => c.group === g).reduce((s, c) => s + Number(c[k] || 0), 0);
        const fixed_m = sum("fixed", "monthly_amount");
        const var_m = sum("variable", "monthly_amount");
        const fixed_a = sum("fixed", "annual_amount");
        const var_a = sum("variable", "annual_amount");
        const emp_ded_m = (local.components || []).filter((c) => c.group === "deduction" && (c.borne_by || "employee") === "employee").reduce((s, c) => s + Number(c.monthly_amount || 0), 0);
        const emp_ded_a_yr = (local.components || []).filter((c) => c.group === "deduction" && (c.borne_by || "employee") === "employee").reduce((s, c) => s + Number(c.annual_amount || 0), 0);
        const employer_ded_a = (local.components || []).filter((c) => c.group === "deduction" && c.borne_by === "employer").reduce((s, c) => s + Number(c.annual_amount || 0), 0);
        const benefits_a = (local.components || []).filter((c) => c.group === "benefit").reduce((s, c) => s + Number(c.annual_amount || 0), 0);
        return {
            gross_monthly: fixed_m + var_m,
            gross_annual: fixed_a + var_a,
            in_hand_monthly: (fixed_m + var_m) - emp_ded_m,
            in_hand_annual: (fixed_a + var_a) - emp_ded_a_yr,
            employer_ctc_annual: fixed_a + var_a + employer_ded_a + benefits_a,
        };
    }, [local.components]);

    const total = dirty ? liveTotals : (local.totals || {});
    const colorCls = COLOR_BAND[local.color] || COLOR_BAND.blue;

    return (
        <div className={`bg-white border border-border border-l-4 ${colorCls} p-4 space-y-3`} data-testid={`scenario-card-${local.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                    <Input value={local.name} onChange={(e) => updateLocal({ name: e.target.value })} disabled={locked} className="rounded-none h-9 font-bold text-base" data-testid={`sc-${local.id}-name`} />
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 flex items-center gap-1.5">Increment %
                            <Input type="number" value={local.increment_pct} onChange={(e) => updateLocal({ increment_pct: Number(e.target.value || 0) })} disabled={locked} className="rounded-none h-7 w-20 text-xs text-right font-mono" data-testid={`sc-${local.id}-inc`} />
                        </label>
                        {!locked && (
                            <button onClick={handleRedistribute} disabled={saving} className="text-[10px] font-mono text-ikb hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid={`sc-${local.id}-redistribute`}>
                                <RefreshCcw className="w-3 h-3" /> Save + Re-distribute
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    {!locked && dirty && (
                        <>
                            <Button onClick={handleSave} disabled={saving} className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3" data-testid={`sc-${local.id}-save`}>
                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                                {saving ? "Saving…" : "Save changes"}
                            </Button>
                            <button onClick={handleDiscard} disabled={saving} className="text-xs font-mono uppercase tracking-widest border border-border bg-white px-2 py-1.5 hover:bg-secondary disabled:opacity-50" data-testid={`sc-${local.id}-discard`}>
                                Discard
                            </button>
                        </>
                    )}
                    {!locked && !dirty && (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/40 px-2" data-testid={`sc-${local.id}-clean`}>Saved</span>
                    )}
                    <button onClick={() => onExport(local.id)} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 hover:bg-secondary inline-flex items-center gap-1" data-testid={`sc-${local.id}-export`}><Download className="w-3 h-3" /> Excel</button>
                    {!locked && <button onClick={() => onDelete(local.id)} className="text-rose-500 hover:bg-rose-50 p-1.5" title="Delete scenario"><Trash2 className="w-4 h-4" /></button>}
                </div>
            </div>

            {/* Totals strip — uses LIVE values while dirty */}
            <div className={`grid grid-cols-2 sm:grid-cols-5 gap-px bg-border border ${dirty ? "border-amber-400" : "border-border"}`} data-testid={`sc-${local.id}-totals`}>
                <div className="bg-white p-2 text-center"><div className="text-[9px] font-mono uppercase text-foreground/50">Gross / month</div><div className="font-display font-black text-base">{fmt(total.gross_monthly)}</div></div>
                <div className="bg-white p-2 text-center"><div className="text-[9px] font-mono uppercase text-foreground/50">Gross / year</div><div className="font-display font-black text-base">{fmt(total.gross_annual)}</div></div>
                <div className="bg-white p-2 text-center"><div className="text-[9px] font-mono uppercase text-foreground/50">In-hand / month</div><div className="font-display font-black text-base text-emerald-700">{fmt(total.in_hand_monthly)}</div></div>
                <div className="bg-white p-2 text-center"><div className="text-[9px] font-mono uppercase text-foreground/50">In-hand / year</div><div className="font-display font-black text-base text-emerald-700">{fmt(total.in_hand_annual)}</div></div>
                <div className="bg-white p-2 text-center"><div className="text-[9px] font-mono uppercase text-foreground/50">Employer CTC</div><div className="font-display font-black text-base text-ikb">{fmt(total.employer_ctc_annual)}</div></div>
            </div>
            {dirty && <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1" data-testid={`sc-${local.id}-dirty-banner`}>Unsaved edits — totals shown are LIVE (not yet persisted). Click "Save changes" to commit.</div>}

            {/* Components grouped */}
            <div className="grid lg:grid-cols-2 gap-3">
                <ScenarioGroup title="Fixed pay" items={grouped.fixed} group="fixed" locked={locked} scenarioId={local.id} onEditComp={editComp} onRemoveComp={removeComp} onAddRow={addRow} />
                <ScenarioGroup title="Variable pay" items={grouped.variable} group="variable" locked={locked} scenarioId={local.id} onEditComp={editComp} onRemoveComp={removeComp} onAddRow={addRow} />
                <ScenarioGroup title="Deductions" items={grouped.deduction} group="deduction" locked={locked} scenarioId={local.id} onEditComp={editComp} onRemoveComp={removeComp} onAddRow={addRow} />
                {(grouped.benefit.length > 0 || !locked) && <ScenarioGroup title="Benefits" items={grouped.benefit} group="benefit" locked={locked} scenarioId={local.id} onEditComp={editComp} onRemoveComp={removeComp} onAddRow={addRow} />}
            </div>

            {/* Justification */}
            <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Justification</label>
                <Textarea value={local.justification || ""} onChange={(e) => updateLocal({ justification: e.target.value })} disabled={locked} placeholder="Why this scenario? e.g. matches candidate's expectation, aligns with internal band 5B." className="rounded-none mt-1 min-h-[60px] text-sm" data-testid={`sc-${local.id}-just`} />
            </div>
        </div>
    );
}

function CreateOfferDialog({ open, onOpenChange, candidate, candidateId, onCreated, prefillOverride }) {
    // Pre-fill candidate financial references from screening_profile so the
    // recruiter doesn't have to retype values already captured upstream.
    // ``prefillOverride`` (used by the Instant Offer flow from
    // QuickTrackDrawer) wins over the screening_profile defaults.
    const sp = candidate?.screening_profile || {};
    const defaultsFromCandidate = () => {
        const ov = prefillOverride || {};
        const current = ov.candidate_current_ctc != null
            ? String(ov.candidate_current_ctc)
            : (sp.current_ctc_annual != null ? String(sp.current_ctc_annual) : "");
        const expected = ov.suggested_ctc != null
            ? String(ov.suggested_ctc)
            : (ov.candidate_expected_ctc != null
                ? String(ov.candidate_expected_ctc)
                : (sp.desired_ctc_annual != null ? String(sp.desired_ctc_annual) : ""));
        let notice = "";
        if (ov.notice_period_days != null) notice = `${ov.notice_period_days} days`;
        else if (sp.notice_period_days != null && sp.notice_period_days !== "") notice = `${sp.notice_period_days} days`;
        const join = sp.earliest_join_date || "";
        // Derived increment %
        let derived = "";
        const curN = Number(current || 0);
        const expN = Number(expected || 0);
        if (curN > 0 && expN > 0) derived = String(Math.round(((expN / curN) - 1) * 1000) / 10);
        else if (sp.desired_increment_pct != null) derived = String(sp.desired_increment_pct);
        return {
            title: ov.title || "Offer round 1",
            candidate_current_ctc: current,
            candidate_expected_ctc: expected,
            candidate_notice_period: notice,
            candidate_earliest_join_date: join,
            candidate_desired_increment_pct: derived,
        };
    };
    const [form, setForm] = useState(defaultsFromCandidate);
    const [busy, setBusy] = useState(false);

    // Re-prefill whenever the dialog opens or candidate switches.
    // Also re-runs when a fresh prefillOverride lands (Instant Offer flow).
    useEffect(() => { if (open) setForm(defaultsFromCandidate()); /* eslint-disable-next-line */ }, [open, candidate?.id, prefillOverride]);

    // Auto-recompute derived increment % when user edits Current/Expected.
    const updateForm = (patch) => {
        const next = { ...form, ...patch };
        if (patch.candidate_current_ctc !== undefined || patch.candidate_expected_ctc !== undefined) {
            const cur = Number(next.candidate_current_ctc || 0);
            const exp = Number(next.candidate_expected_ctc || 0);
            if (cur > 0 && exp > 0) {
                next.candidate_desired_increment_pct = String(Math.round(((exp / cur) - 1) * 1000) / 10);
            } else {
                // Clear stale derived value when either side is missing.
                next.candidate_desired_increment_pct = "";
            }
        }
        setForm(next);
    };

    const submit = async () => {
        if (!form.candidate_current_ctc) { toast.error("Current CTC is required"); return; }
        setBusy(true);
        try {
            const payload = {
                candidate_id: candidateId,
                title: form.title,
                candidate_current_ctc: Number(form.candidate_current_ctc),
                candidate_expected_ctc: form.candidate_expected_ctc ? Number(form.candidate_expected_ctc) : null,
                candidate_notice_period: form.candidate_notice_period || null,
                candidate_earliest_join_date: form.candidate_earliest_join_date || null,
                candidate_desired_increment_pct: form.candidate_desired_increment_pct ? Number(form.candidate_desired_increment_pct) : null,
            };
            const { data } = await api.post("/offers", payload);
            toast.success("Offer draft created");
            onCreated(data);
            onOpenChange(false);
            setForm(defaultsFromCandidate());
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const hasPrefill = !!(sp.current_ctc_annual || sp.desired_ctc_annual || sp.notice_period_days || sp.earliest_join_date);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md rounded-none" data-testid="create-offer-dialog">
                <DialogHeader>
                    <DialogTitle>New offer</DialogTitle>
                    <DialogDescription>
                        Start a new offer bundle for this candidate.
                        {hasPrefill && <span className="block mt-1 text-emerald-700">Imported from candidate screening profile — edit if needed.</span>}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Input value={form.title} onChange={(e) => updateForm({ title: e.target.value })} placeholder="Offer title (e.g. Round 1)" className="rounded-none h-10" data-testid="offer-create-title" />
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Current CTC (annual, ₹) *</label>
                        <Input type="number" value={form.candidate_current_ctc} onChange={(e) => updateForm({ candidate_current_ctc: e.target.value })} placeholder="e.g. 1200000" className="rounded-none h-10 mt-1" data-testid="offer-create-current-ctc" />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Desired CTC (annual, ₹)</label>
                        <Input type="number" value={form.candidate_expected_ctc} onChange={(e) => updateForm({ candidate_expected_ctc: e.target.value })} placeholder="e.g. 1800000" className="rounded-none h-10 mt-1" data-testid="offer-create-expected-ctc" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Increment % (derived)</label>
                            <Input type="number" value={form.candidate_desired_increment_pct} onChange={(e) => updateForm({ candidate_desired_increment_pct: e.target.value })} placeholder="auto" className="rounded-none h-10 mt-1" data-testid="offer-create-increment-pct" />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Notice period</label>
                            <Input value={form.candidate_notice_period} onChange={(e) => updateForm({ candidate_notice_period: e.target.value })} placeholder="e.g. 60 days" className="rounded-none h-10 mt-1" data-testid="offer-create-notice" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Earliest join date</label>
                        <Input type="date" value={form.candidate_earliest_join_date} onChange={(e) => updateForm({ candidate_earliest_join_date: e.target.value })} className="rounded-none h-10 mt-1" data-testid="offer-create-join-date" />
                    </div>
                    <Button onClick={submit} disabled={busy} className="w-full rounded-none bg-ikb hover:bg-[#001f7a] text-white h-10" data-testid="offer-create-submit">{busy ? "Creating…" : "Create draft"}</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function ApprovalActionDialog({ open, onOpenChange, mode, onConfirm }) {
    // mode: "approve" | "reject" | "accept" | "decline"
    const [comment, setComment] = useState("");
    useEffect(() => { if (open) setComment(""); }, [open]);
    const labels = {
        approve: { title: "Approve offer", action: "Approve", color: "bg-emerald-600 hover:bg-emerald-700", placeholder: "Note for the recruiter (optional)" },
        reject: { title: "Reject offer", action: "Reject", color: "bg-rose-600 hover:bg-rose-700", placeholder: "Why? (visible to recruiter)" },
    }[mode] || {};
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md rounded-none" data-testid={`offer-${mode}-dialog`}>
                <DialogHeader><DialogTitle>{labels.title}</DialogTitle></DialogHeader>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={labels.placeholder} className="rounded-none min-h-[100px]" data-testid={`offer-${mode}-comment`} />
                <Button onClick={() => onConfirm(comment)} className={`rounded-none text-white h-10 ${labels.color}`} data-testid={`offer-${mode}-confirm`}>{labels.action}</Button>
            </DialogContent>
        </Dialog>
    );
}

function CandidateOutcomeDialog({ open, onOpenChange, offer, onConfirm }) {
    const [outcome, setOutcome] = useState("accepted");
    const [sid, setSid] = useState(offer?.scenarios?.[0]?.id || "");
    const [notes, setNotes] = useState("");
    useEffect(() => { if (open) { setOutcome("accepted"); setSid(offer?.scenarios?.[0]?.id || ""); setNotes(""); } }, [open, offer]);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md rounded-none" data-testid="candidate-outcome-dialog">
                <DialogHeader>
                    <DialogTitle>Record candidate's response</DialogTitle>
                    <DialogDescription>What did the candidate say?</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="inline-flex border border-border bg-white">
                        {["accepted", "declined", "negotiating"].map((o) => (
                            <button key={o} onClick={() => setOutcome(o)} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${outcome === o ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid={`outcome-${o}`}>{o}</button>
                        ))}
                    </div>
                    {outcome === "accepted" && (
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-1">Which scenario was accepted?</div>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {(offer?.scenarios || []).map((s) => (
                                    <label key={s.id} className={`flex items-center gap-2 border p-2 cursor-pointer hover:bg-secondary/30 ${sid === s.id ? "border-ikb bg-blue-50" : "border-border"}`} data-testid={`outcome-pick-${s.id}`}>
                                        <input type="radio" name="sid" checked={sid === s.id} onChange={() => setSid(s.id)} className="accent-ikb" />
                                        <span className="text-sm flex-1">{s.name}</span>
                                        <span className="text-[10px] font-mono text-foreground/60">{fmt(s.totals?.employer_ctc_annual)}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="rounded-none min-h-[60px]" data-testid="outcome-notes" />
                    <Button onClick={() => onConfirm({ scenario_id: outcome === "accepted" ? sid : null, outcome, notes })} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white w-full h-10" data-testid="outcome-confirm">Record outcome</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function ComparisonView({ offer, currentCtc }) {
    const scenarios = offer.scenarios || [];
    const Row = ({ label, getter, highlight, fmtter = fmt, accent }) => {
        const values = scenarios.map((s) => getter(s));
        const max = Math.max(...values.filter((v) => typeof v === "number"));
        const min = Math.min(...values.filter((v) => typeof v === "number"));
        return (
            <tr className={`border-b border-border/50 ${highlight ? "bg-secondary/30" : ""}`} data-testid={`cmp-row-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className="px-3 py-2 text-xs font-medium text-foreground/80 sticky left-0 bg-inherit border-r border-border">{label}</td>
                {values.map((v, i) => {
                    const isMax = typeof v === "number" && v === max && max !== min;
                    const isMin = typeof v === "number" && v === min && max !== min;
                    const accentCls = accent === "high" && isMax ? "text-emerald-700 font-bold" : accent === "low" && isMin ? "text-emerald-700 font-bold" : accent === "low" && isMax ? "text-rose-700" : "";
                    return (
                        <td key={i} className={`px-3 py-2 text-sm font-mono text-right tabular-nums ${accentCls}`}>
                            {typeof v === "number" ? fmtter(v) : (v ?? "—")}
                        </td>
                    );
                })}
            </tr>
        );
    };
    const pctVsCurrent = (annual) => currentCtc > 0 ? `${((annual / currentCtc - 1) * 100).toFixed(1)}%` : "—";
    return (
        <div className="border border-border bg-white overflow-x-auto" data-testid="comparison-view">
            <table className="w-full min-w-[640px]">
                <thead className="bg-foreground text-background">
                    <tr>
                        <th className="px-3 py-2.5 text-left text-[10px] font-mono uppercase tracking-widest sticky left-0 bg-foreground border-r border-foreground/40">Metric</th>
                        {scenarios.map((s) => {
                            const colorCls = { blue: "border-ikb", emerald: "border-emerald-500", rose: "border-rose-500", amber: "border-amber-500", violet: "border-violet-500" }[s.color] || "border-border";
                            return (
                                <th key={s.id} className={`px-3 py-2.5 text-right border-b-4 ${colorCls}`} data-testid={`cmp-col-${s.id}`}>
                                    <div className="text-sm font-display font-black">{s.name}</div>
                                    {s.preset_key && <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">{s.preset_key.replace(/_/g, " ")}</div>}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    <Row label="Increment %" getter={(s) => s.increment_pct} fmtter={(v) => `+${v}%`} highlight accent="high" />
                    <Row label="In-hand · Monthly" getter={(s) => s.totals?.in_hand_monthly} accent="high" />
                    <Row label="In-hand · Annual" getter={(s) => s.totals?.in_hand_annual} highlight accent="high" />
                    <Row label="Gross · Monthly" getter={(s) => s.totals?.gross_monthly} />
                    <Row label="Gross · Annual" getter={(s) => s.totals?.gross_annual} highlight />
                    <Row label="Fixed · Annual" getter={(s) => s.totals?.fixed_annual} />
                    <Row label="Variable · Annual" getter={(s) => s.totals?.variable_annual} highlight />
                    <Row label="Employee Deductions · Monthly" getter={(s) => s.totals?.employee_deductions_monthly} accent="low" />
                    <Row label="Employer Cost / CTC" getter={(s) => s.totals?.employer_ctc_annual} highlight accent="low" />
                    <Row label="Δ vs current CTC" getter={(s) => s.totals?.employer_ctc_annual} fmtter={pctVsCurrent} />
                    <tr className="border-t-2 border-foreground/20">
                        <td className="px-3 py-3 text-[10px] font-mono uppercase tracking-widest text-foreground/60 sticky left-0 bg-white border-r border-border align-top">Justification</td>
                        {scenarios.map((s) => (
                            <td key={s.id} className="px-3 py-3 text-xs text-foreground/80 leading-relaxed align-top max-w-[280px] whitespace-pre-wrap" data-testid={`cmp-just-${s.id}`}>
                                {s.justification || <span className="text-foreground/40">—</span>}
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

const NEG_CATEGORY_COLOR = {
    fixed: "border-l-ikb bg-blue-50/30",
    variable: "border-l-emerald-500 bg-emerald-50/30",
    benefits: "border-l-violet-500 bg-violet-50/30",
    deductions: "border-l-amber-500 bg-amber-50/30",
    growth: "border-l-teal-500 bg-teal-50/30",
    risk: "border-l-rose-500 bg-rose-50/30",
};

function NegotiationPanel({ points, onPatch, onDelete, locked }) {
    const agreed = points.filter((p) => p.candidate_agreed).length;
    return (
        <div className="border border-border bg-white" data-testid="negotiation-panel">
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-700" />
                    <h3 className="font-display font-bold text-base text-amber-800">Negotiation talking points</h3>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700/70">{agreed}/{points.length} agreed</span>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700/60">AI-generated · use during candidate conversation</span>
            </div>
            <div className="divide-y divide-border">
                {points.map((p, i) => {
                    const cls = NEG_CATEGORY_COLOR[p.category] || "border-l-border";
                    return (
                        <div key={p.id} className={`border-l-4 p-3 sm:p-4 ${cls}`} data-testid={`neg-${p.id}`}>
                            <div className="flex items-start gap-3">
                                <label className="flex items-center pt-1 shrink-0">
                                    <input
                                        type="checkbox"
                                        checked={!!p.candidate_agreed}
                                        onChange={(e) => onPatch(p.id, { candidate_agreed: e.target.checked })}
                                        className="w-4 h-4 accent-ikb"
                                        data-testid={`neg-${p.id}-agreed`}
                                    />
                                </label>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[9px] font-mono uppercase tracking-widest bg-white border border-border px-1.5 py-0.5 text-foreground/60">{i + 1} · {p.category}</span>
                                        <div className="font-display font-bold text-sm">{p.title}</div>
                                    </div>
                                    <p className={`text-sm leading-relaxed mt-1 ${p.candidate_agreed ? "text-foreground/60" : "text-foreground/85"}`}>{p.point}</p>
                                    <textarea
                                        value={p.notes || ""}
                                        onChange={(e) => onPatch(p.id, { notes: e.target.value })}
                                        placeholder="Notes from the conversation (e.g. countered with ₹X, decision deferred…)"
                                        className="w-full mt-2 border border-border rounded-none bg-white text-xs p-2 min-h-[40px]"
                                        data-testid={`neg-${p.id}-notes`}
                                    />
                                </div>
                                {!locked && (
                                    <button onClick={() => onDelete(p.id)} className="text-rose-500 hover:bg-rose-50 p-1.5 shrink-0" title="Delete" data-testid={`neg-${p.id}-del`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function OffersForCandidate() {
    const { id: candidateId } = useParams();
    const { user } = useAuth();
    const isEmployer = ["employer", "admin"].includes(user?.role);

    const [candidate, setCandidate] = useState(null);
    const [settings, setSettings] = useState(null);
    const [offers, setOffers] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [prefillOverride, setPrefillOverride] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [approveDialog, setApproveDialog] = useState(null); // "approve"|"reject"|null
    const [outcomeOpen, setOutcomeOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [viewMode, setViewMode] = useState("detail");   // "detail" | "compare"
    const [aiBusy, setAiBusy] = useState(false);
    const [negBusy, setNegBusy] = useState(false);

    // ─── Instant Offer entry point ──────────────────────────────────────
    // QuickTrackDrawer navigates here with `?instant_offer=1&suggested_ctc=…
    // &current_ctc=…&notice_days=…` — auto-opens the create dialog with
    // those values pre-filled. We strip the params on mount so a reload
    // doesn't re-trigger the dialog.
    useEffect(() => {
        if (searchParams.get("instant_offer") !== "1") return;
        const override = {
            suggested_ctc: searchParams.get("suggested_ctc"),
            candidate_current_ctc: searchParams.get("current_ctc"),
            candidate_expected_ctc: searchParams.get("expected_ctc"),
            notice_period_days: searchParams.get("notice_days"),
            title: searchParams.get("title") || "Instant offer",
        };
        setPrefillOverride(override);
        setShowCreate(true);
        // Strip the params — one-shot trigger.
        const next = new URLSearchParams(searchParams);
        ["instant_offer", "suggested_ctc", "current_ctc", "expected_ctc", "notice_days", "title"].forEach((k) => next.delete(k));
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const load = async () => {
        setLoading(true);
        try {
            const [c, s, o] = await Promise.all([
                api.get(`/candidates/${candidateId}`).then((r) => r.data).catch(() => null),
                api.get("/offer-settings").then((r) => r.data),
                api.get(`/candidates/${candidateId}/offers`).then((r) => r.data.items || []),
            ]);
            setCandidate(c);
            setSettings(s);
            setOffers(o);
            if (o.length && !activeId) setActiveId(o[0].id);
            if (!o.length) setActiveId(null);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId]);

    const active = useMemo(() => offers.find((o) => o.id === activeId), [offers, activeId]);
    const isLocked = ["approved", "accepted", "declined", "rejected"].includes(active?.status);

    // ----- Mutations -----
    const generateScenario = async (preset) => {
        setBusy(true);
        try {
            await api.post(`/offers/${active.id}/scenarios/generate`, { preset_key: preset.key, justification: "" });
            await load();
            toast.success(`${preset.label} scenario added`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const aiRecommend = async () => {
        setAiBusy(true);
        try {
            await api.post(`/offers/${active.id}/scenarios/ai-recommend`);
            await load();
            toast.success("AI scenario added");
            setViewMode("detail");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setAiBusy(false); }
    };

    const generateNegotiationPoints = async () => {
        setNegBusy(true);
        try {
            const { data } = await api.post(`/offers/${active.id}/negotiation-points/generate`);
            await load();
            toast.success(`${data.added} negotiation points added`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setNegBusy(false); }
    };

    const patchNegotiationPoint = async (pid, patch) => {
        try {
            await api.patch(`/offers/${active.id}/negotiation-points/${pid}`, patch);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const deleteNegotiationPoint = async (pid) => {
        if (!window.confirm("Remove this negotiation point?")) return;
        await api.delete(`/offers/${active.id}/negotiation-points/${pid}`);
        await load();
    };

    const exportComparison = async () => {
        try {
            const r = await api.get(`/offers/${active.id}/compare.xlsx`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `offer-comparison-${candidate?.name || "candidate"}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Comparison downloaded");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    // Explicit save handler used by ScenarioCard's "Save changes" button.
    // Pushes the LOCAL edit state in one PATCH and reloads from server.
    const saveScenario = async (scenarioId, patch) => {
        try {
            await api.patch(`/offers/${active.id}/scenarios/${scenarioId}`, patch);
            await load();
            toast.success("Scenario saved");
        } catch (err) {
            toast.error(formatApiError(err));
            throw err;
        }
    };

    // Save + redistribute (single round-trip). Triggered from ScenarioCard's
    // "Save + Re-distribute" button.
    const redistributeScenario = async (scenarioId, incrementPct) => {
        try {
            await api.patch(`/offers/${active.id}/scenarios/${scenarioId}`, {
                increment_pct: Number(incrementPct || 0),
                redistribute: true,
            });
            await load();
            toast.success("Re-distributed");
        } catch (err) {
            toast.error(formatApiError(err));
            throw err;
        }
    };

    const deleteScenario = async (scenarioId) => {
        if (!window.confirm("Delete this scenario?")) return;
        await api.delete(`/offers/${active.id}/scenarios/${scenarioId}`);
        await load();
    };

    const exportScenario = async (scenarioId) => {
        try {
            const r = await api.get(`/offers/${active.id}/scenarios/${scenarioId}/export.xlsx`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `offer-${candidate?.name || "candidate"}-${scenarioId}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const sendForApproval = async () => {
        await api.post(`/offers/${active.id}/send-for-approval`);
        toast.success("Sent for approval");
        await load();
    };

    const onApprovalAction = async (mode, comment) => {
        await api.post(`/offers/${active.id}/${mode}`, { comment });
        toast.success(mode === "approve" ? "Approved" : "Rejected");
        setApproveDialog(null);
        await load();
    };

    const onCandidateOutcome = async (payload) => {
        await api.post(`/offers/${active.id}/candidate-outcome`, payload);
        toast.success("Outcome recorded");
        setOutcomeOpen(false);
        await load();
    };

    if (loading || !settings) return <div className="p-10 text-center text-sm text-muted-foreground">Loading offers…</div>;

    const presets = settings.scenario_presets || [];
    const statusPill = STATUS_PILL[active?.status] || STATUS_PILL.draft;

    return (
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4" data-testid="offers-page">
            <div className="flex items-center gap-3 flex-wrap">
                <Link to={`/candidates/${candidateId}`} className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-ikb" data-testid="offers-back-to-candidate">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to candidate
                </Link>
                <div className="ml-2">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Offers</div>
                    <h1 className="font-display font-black text-xl sm:text-2xl">{candidate?.name || "Candidate"}</h1>
                </div>
                <Button onClick={() => setShowCreate(true)} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9 ml-auto" data-testid="offers-new-btn">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> New offer
                </Button>
            </div>

            {/* Offer tabs */}
            {offers.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="offers-tabs">
                    {offers.map((o) => {
                        const pill = STATUS_PILL[o.status] || STATUS_PILL.draft;
                        return (
                            <button
                                key={o.id}
                                onClick={() => setActiveId(o.id)}
                                className={`text-xs border px-3 py-1.5 inline-flex items-center gap-2 ${activeId === o.id ? "bg-foreground text-background border-foreground" : "bg-white border-border hover:bg-secondary"}`}
                                data-testid={`offer-tab-${o.id}`}
                            >
                                <span className="font-medium">{o.title}</span>
                                <span className={`text-[9px] font-mono uppercase tracking-widest border px-1 py-0.5 ${pill.cls}`}>{pill.label}</span>
                                <span className="text-[10px] font-mono opacity-70">{(o.scenarios || []).length}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {!active && (
                <div className="border border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="offers-empty">
                    No offers yet. Click "New offer" to start.
                </div>
            )}

            {active && (
                <>
                    {/* Header strip with status + workflow actions */}
                    <div className="border border-border bg-white p-4 space-y-3" data-testid={`offer-header-${active.id}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="font-display font-bold text-lg">{active.title}</h2>
                                <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${statusPill.cls}`}>{statusPill.label}</span>
                                {active.created_by_name && <span className="text-[10px] font-mono text-foreground/50">created by {active.created_by_name}</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {active.status === "draft" && (
                                    <Button onClick={sendForApproval} disabled={busy || !(active.scenarios || []).length} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9" data-testid="offer-send-approval"><Send className="w-3.5 h-3.5 mr-1.5" /> Send for approval</Button>
                                )}
                                {active.status === "awaiting_approval" && isEmployer && (
                                    <>
                                        <Button onClick={() => setApproveDialog("approve")} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white h-9" data-testid="offer-approve-btn"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approve</Button>
                                        <Button variant="outline" onClick={() => setApproveDialog("reject")} className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50 h-9" data-testid="offer-reject-btn"><XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject</Button>
                                    </>
                                )}
                                {active.status === "approved" && (
                                    <Button onClick={() => setOutcomeOpen(true)} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9" data-testid="offer-record-outcome"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Record candidate outcome</Button>
                                )}
                                {(active.status === "rejected" || active.status === "negotiating") && (
                                    <Button onClick={sendForApproval} className="rounded-none h-9" variant="outline" data-testid="offer-resend"><Send className="w-3.5 h-3.5 mr-1.5" /> Resend for approval</Button>
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
                            <div><div className="text-[9px] font-mono uppercase text-foreground/50">Current CTC</div><div className="font-mono">{fmtFull(active.candidate_current_ctc)}/yr</div></div>
                            <div><div className="text-[9px] font-mono uppercase text-foreground/50">Desired CTC</div><div className="font-mono">{active.candidate_expected_ctc ? fmtFull(active.candidate_expected_ctc) + "/yr" : "—"}</div></div>
                            <div>
                                <div className="text-[9px] font-mono uppercase text-foreground/50">Derived increment</div>
                                <div className="font-mono">
                                    {(() => {
                                        if (active.candidate_desired_increment_pct != null) return `+${active.candidate_desired_increment_pct}%`;
                                        const cur = Number(active.candidate_current_ctc || 0);
                                        const exp = Number(active.candidate_expected_ctc || 0);
                                        if (cur > 0 && exp > 0) return `+${(((exp / cur) - 1) * 100).toFixed(1)}%`;
                                        return "—";
                                    })()}
                                </div>
                            </div>
                            <div><div className="text-[9px] font-mono uppercase text-foreground/50">Notice</div><div className="font-mono">{active.candidate_notice_period || "—"}</div></div>
                            <div><div className="text-[9px] font-mono uppercase text-foreground/50">Earliest join</div><div className="font-mono">{active.candidate_earliest_join_date || "—"}</div></div>
                            <div><div className="text-[9px] font-mono uppercase text-foreground/50">Scenarios</div><div className="font-mono">{(active.scenarios || []).length}</div></div>
                        </div>
                        {active.approval && (
                            <div className="border-l-2 border-emerald-500 bg-emerald-50/40 p-2 text-xs" data-testid="offer-approval-block">
                                <b>Approved</b> by {active.approval.approver_name} · {new Date(active.approval.approved_at).toLocaleString()}
                                {active.approval.comment && <div className="text-foreground/70 mt-0.5">"{active.approval.comment}"</div>}
                            </div>
                        )}
                        {active.rejected && (
                            <div className="border-l-2 border-rose-500 bg-rose-50/40 p-2 text-xs" data-testid="offer-rejected-block">
                                <b>Rejected</b> by {active.rejected.approver_name} · {new Date(active.rejected.rejected_at).toLocaleString()}
                                {active.rejected.comment && <div className="text-foreground/70 mt-0.5">"{active.rejected.comment}"</div>}
                            </div>
                        )}
                        {active.candidate_outcome && (
                            <div className="border-l-2 border-ikb bg-blue-50/40 p-2 text-xs" data-testid="offer-outcome-block">
                                <b>Candidate {active.candidate_outcome.outcome}</b>
                                {active.candidate_outcome.scenario_id && <span> · scenario: {(active.scenarios.find((s) => s.id === active.candidate_outcome.scenario_id) || {}).name}</span>}
                                <span> · {new Date(active.candidate_outcome.marked_at).toLocaleString()}</span>
                                {active.candidate_outcome.notes && <div className="text-foreground/70 mt-0.5">"{active.candidate_outcome.notes}"</div>}
                            </div>
                        )}
                    </div>

                    {/* View toggle + AI helpers */}
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="offers-toolbar">
                        <div className="inline-flex border border-border bg-white" data-testid="offers-view-toggle">
                            <button onClick={() => setViewMode("detail")} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${viewMode === "detail" ? "bg-foreground text-background" : "hover:bg-secondary"}`} data-testid="offers-view-detail"><ListChecks className="w-3 h-3" /> Detail</button>
                            <button onClick={() => setViewMode("compare")} disabled={(active.scenarios || []).length === 0} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-border inline-flex items-center gap-1.5 ${viewMode === "compare" ? "bg-foreground text-background" : "hover:bg-secondary"} disabled:opacity-40`} data-testid="offers-view-compare"><BarChart3 className="w-3 h-3" /> Compare</button>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {!isLocked && (
                                <button onClick={aiRecommend} disabled={aiBusy} className="text-xs font-mono uppercase tracking-widest border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="offers-ai-recommend">
                                    {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                                    {aiBusy ? "Generating…" : "AI recommended offer"}
                                </button>
                            )}
                            {(active.scenarios || []).length > 0 && (
                                <button onClick={generateNegotiationPoints} disabled={negBusy} className="text-xs font-mono uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="offers-gen-negotiation">
                                    {negBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                    {negBusy ? "Thinking…" : "Generate negotiation points"}
                                </button>
                            )}
                            {(active.scenarios || []).length > 0 && (
                                <button onClick={exportComparison} className="text-xs font-mono uppercase tracking-widest border border-border bg-white px-3 py-1.5 hover:bg-secondary inline-flex items-center gap-1.5" data-testid="offers-export-comparison">
                                    <FileSpreadsheet className="w-3 h-3" /> Export comparison
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Scenario add bar */}
                    {!isLocked && viewMode === "detail" && (
                        <div className="border border-border bg-white p-3 flex items-center gap-2 flex-wrap" data-testid="offer-add-scenarios">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Add scenario from preset:</span>
                            {presets.map((p) => (
                                <button
                                    key={p.key}
                                    onClick={() => generateScenario(p)}
                                    disabled={busy}
                                    className={`text-xs px-2.5 py-1 border border-border bg-white hover:bg-secondary inline-flex items-center gap-1.5`}
                                    title={p.description}
                                    data-testid={`offer-preset-${p.key}`}
                                >
                                    <Sparkles className="w-3 h-3 text-ikb" /> {p.label}
                                    <span className="text-[9px] font-mono text-foreground/50">+{p.increment_pct}%</span>
                                </button>
                            ))}
                            <button
                                onClick={() => generateScenario({ key: null, label: "Custom" })}
                                disabled={busy}
                                className="text-xs px-2.5 py-1 border border-dashed border-border bg-white hover:bg-secondary inline-flex items-center gap-1"
                                data-testid="offer-preset-custom"
                            >
                                <Plus className="w-3 h-3" /> Custom
                            </button>
                        </div>
                    )}

                    {/* Scenarios — detail view */}
                    {viewMode === "detail" && (
                    <div className="space-y-4">
                        {(active.scenarios || []).map((sc) => (
                            <ScenarioCard
                                key={sc.id}
                                scenario={sc}
                                onSave={saveScenario}
                                onRedistribute={redistributeScenario}
                                onDelete={(sid) => deleteScenario(sid)}
                                onExport={exportScenario}
                                locked={isLocked}
                            />
                        ))}
                        {(active.scenarios || []).length === 0 && (
                            <div className="border border-dashed border-border bg-white p-8 text-center text-sm text-muted-foreground" data-testid="offer-no-scenarios">
                                No scenarios yet. Pick a preset above to generate one — components will be auto-distributed from the candidate's current CTC.
                            </div>
                        )}
                    </div>
                    )}

                    {/* Scenarios — comparison view */}
                    {viewMode === "compare" && (
                        <ComparisonView offer={active} currentCtc={active.candidate_current_ctc} />
                    )}

                    {/* Negotiation talking points */}
                    {(active.negotiation_points || []).length > 0 && (
                        <NegotiationPanel
                            points={active.negotiation_points}
                            onPatch={patchNegotiationPoint}
                            onDelete={deleteNegotiationPoint}
                            locked={isLocked}
                        />
                    )}
                </>
            )}

            <CreateOfferDialog open={showCreate} onOpenChange={(v) => { setShowCreate(v); if (!v) setPrefillOverride(null); }} candidate={candidate} candidateId={candidateId} prefillOverride={prefillOverride} onCreated={(o) => { load(); setActiveId(o.id); }} />
            <ApprovalActionDialog open={!!approveDialog} onOpenChange={(v) => { if (!v) setApproveDialog(null); }} mode={approveDialog} onConfirm={(c) => onApprovalAction(approveDialog, c)} />
            <CandidateOutcomeDialog open={outcomeOpen} onOpenChange={setOutcomeOpen} offer={active} onConfirm={onCandidateOutcome} />
        </div>
    );
}
